import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ClothedEyeError from './ClothedEyeError';

/**
 * React Error Boundary with ClothedEye Branding
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the errors, and displays a fallback UI with branding
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and error reporting service
    console.error('🔥 React Error Boundary caught an error:', error, errorInfo);

    // Store error details in state
    this.setState({
      error,
      errorInfo,
    });

    // You could also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Render branded error UI
      return (
        <ClothedEyeError
          errorType="boundary"
          title="Component Error"
          description={
            this.props.showErrorDetails && this.state.error
              ? `${this.state.error.toString()} - Please refresh the page or try again.`
              : 'A component crashed unexpectedly. Please refresh the page.'
          }
          onRetry={this.handleReset}
          onGoHome={() => {
            this.handleReset();
            window.location.href = '/';
          }}
          fullscreen={true}
          size="medium"
          theme="dark"
        />
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  showErrorDetails: PropTypes.bool,
};

export default ErrorBoundary;
